import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Loader2, FileSpreadsheet, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { MileageLegs } from "./MileageLegs";
import { Reimbursements, ReimbursementsResult } from "./Reimbursements";

const usd = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });
const shiftDays = (iso, days) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const isFailed = (r) => String(r.status || "").trim().toLowerCase() === "failed site visit";
const jobKey = (r) => String(r.job || r.job_id || r.job_address || r.key).trim().toLowerCase();

export const CostReportDialog = ({ open, onOpenChange, currentDate }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState(null);
  const [rates, setRates] = useState(null);
  const [home, setHome] = useState(null);
  const [sel, setSel] = useState({});       // key -> { include, job_fee }
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(() => localStorage.getItem("dfr_cost_hourly_rate") || "");
  const [reimb, setReimb] = useState([]);
  const [legs, setLegs] = useState([]); // day travel legs (Home → job, job → job, job → Home) from the travel plan / addresses

  useEffect(() => {
    if (!open) return;
    const base = currentDate || new Date().toISOString().slice(0, 10);
    setTo(base);
    setFrom(shiftDays(base, -6));
    setRows(null);
    setResult(null);
    setLegs([]);
    setReimb([]);
  }, [open, currentDate]);

  const loadRows = async () => {
    if (!from || !to) return;
    setLoading(true);
    setResult(null);
    try {
      const params = { params: { date_from: from, date_to: to } };
      const [{ data }, rb] = await Promise.all([axios.get("/field-reports/cost-report/rows", params), axios.get("/field-reports/cost-report/reimbursements", params)]);
      setReimb(rb.data || []);
      setRows(data.rows || []);
      setRates(data.rates || null);
      setHome(data.home || null);
      // every row included; the $75 job fee defaults ON only for a job's first row with footage — OFF for zero footage and repeats
      const seen = new Set();
      const next = {};
      [...(data.rows || [])].sort((a, b) => a.date.localeCompare(b.date) || (a.order ?? 0) - (b.order ?? 0)).forEach((r) => {
        const k = jobKey(r);
        const fee = !isFailed(r) && (Number(r.footage) || 0) > 0 && !seen.has(k);
        next[r.key] = { include: true, job_fee: fee };
        if (fee) seen.add(k);
      });
      setSel(next);
      setLegs(data.legs || []);
    } catch { toast.error("Could not load job rows"); }
    finally { setLoading(false); }
  };

  const chosen = useMemo(() => (rows || []).filter((r) => sel[r.key]?.include).sort((a, b) => a.date.localeCompare(b.date) || (a.order ?? 0) - (b.order ?? 0)), [rows, sel]);
  const minLeg = rates?.min_leg_miles ?? 25;
  const feeFor = (r) => (String(r.job_type || "").trim().toUpperCase() === "PBB" ? rates?.pbb_job_fee ?? 50 : rates?.job_fee ?? 75);
  const chosenDates = useMemo(() => new Set(chosen.map((r) => r.date)), [chosen]);
  const shownLegs = useMemo(() => legs.filter((g) => chosenDates.has(g.date)), [legs, chosenDates]);
  const dupKeys = useMemo(() => {
    const c = {};
    (rows || []).forEach((r) => { c[jobKey(r)] = (c[jobKey(r)] || 0) + 1; });
    return new Set(Object.keys(c).filter((k) => c[k] > 1));
  }, [rows]);

  const rate = Math.max(0, parseFloat(hourlyRate) || 0);
  const payload = () => {
    localStorage.setItem("dfr_cost_hourly_rate", hourlyRate); // remembered as the starting value next time, still editable
    return { date_from: from, date_to: to, hourly_rate: rate, lines: chosen.map((r) => ({ ...r, job_fee: !!sel[r.key]?.job_fee })), legs: shownLegs, reimbursements: reimb.map((i) => ({ ...i, amount: Math.max(0, parseFloat(i.amount) || 0) })) };
  };

  const generate = async () => {
    if (!chosen.length && !reimb.length) return toast.error("Select at least one job or add a reimbursement");
    setBusy(true);
    try { setResult((await axios.post("/field-reports/cost-report", payload())).data); }
    catch { toast.error("Could not generate the cost report"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.post("/field-reports/cost-report", payload(), { params: { format: "xlsx" }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `Cost_Report_${from}_to_${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  const setAll = (include) => setSel((s) => Object.fromEntries(Object.entries(s).map(([k, v]) => [k, { ...v, include }])));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="cost-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" />Cost Report</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {rates ? `$${rates.job_fee} per job ($${rates.pbb_job_fee ?? 50} for PBB, toggle per row) · $${rates.failed_visit_fee ?? 35} per Failed Site Visit · $${rates.per_pole.toFixed(2)} per pole · $${rates.per_mile} per mile after the first ${minLeg} mi of each day · $${rates.per_foot} per ft over ${rates.free_feet.toLocaleString()} ft`
              : "Pick a date range, choose the jobs to include, then generate the pay breakdown."}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3 min-w-0 max-w-full">
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-zinc-600">From<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40 mt-1" data-testid="cost-from" /></label>
              <label className="text-xs text-zinc-600">To<Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40 mt-1" data-testid="cost-to" /></label>
              <Button onClick={loadRows} disabled={loading || !from || !to} className="h-9 text-xs" data-testid="cost-load-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load jobs"}
              </Button>
              {rows && rows.length > 0 && (
                <div className="ml-auto flex gap-1">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAll(true)} data-testid="cost-select-all">All</Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAll(false)} data-testid="cost-select-none">None</Button>
                </div>
              )}
            </div>

            {rows && rows.length === 0 && <p className="text-sm text-zinc-500" data-testid="cost-empty">No job rows on your reports between those dates.</p>}

            {rows && rows.length > 0 && (
              <div className="border rounded-lg overflow-x-auto min-w-0 max-w-full">
                <table className="w-full text-xs" data-testid="cost-pick-table">
                  <thead className="bg-zinc-100 text-zinc-600">
                    <tr>
                      <th className="p-2 text-left w-10">Incl.</th>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Job</th>
                      <th className="p-2 text-left">Address</th>
                      <th className="p-2 text-right">Poles</th>
                      <th className="p-2 text-right">Miles</th>
                      <th className="p-2 text-right">Footage</th>
                      <th className="p-2 text-right">Hours</th>
                      <th className="p-2 text-center">Job fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const s = sel[r.key] || {};
                      const dup = dupKeys.has(jobKey(r));
                      return (
                        <tr key={r.key} className={`border-t ${s.include ? "" : "opacity-50"}`} data-testid={`cost-row-${i}`}>
                          <td className="p-2"><Checkbox checked={!!s.include} onCheckedChange={(v) => setSel((p) => ({ ...p, [r.key]: { ...p[r.key], include: !!v } }))} data-testid={`cost-include-${i}`} /></td>
                          <td className="p-2 whitespace-nowrap">{r.date}</td>
                          <td className="p-2 font-medium">{r.job || "—"}{dup && <span className="ml-1 text-[10px] text-amber-600 font-normal" data-testid={`cost-dup-${i}`}>repeat</span>}{isFailed(r) && <span className="ml-1 text-[10px] text-red-600 font-semibold whitespace-nowrap" data-testid={`cost-failed-${i}`}>Failed visit +${rates?.failed_visit_fee ?? 35}</span>}</td>
                          <td className="p-2 text-zinc-600 max-w-[260px] truncate" title={r.job_address}>{r.job_address}</td>
                          <td className="p-2 text-right">{num(r.poles)}</td>
                          <td className="p-2 text-right">{num(r.miles)}</td>
                          <td className="p-2 text-right">{num(r.footage)}</td>
                          <td className="p-2 text-right">{num(r.hours)}</td>
                          <td className="p-2 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <Switch checked={!!s.job_fee} disabled={!s.include} onCheckedChange={(v) => setSel((p) => ({ ...p, [r.key]: { ...p[r.key], job_fee: !!v } }))} data-testid={`cost-jobfee-${i}`} />
                              <span className={`text-[11px] tabular-nums ${s.job_fee ? "text-zinc-800 font-semibold" : "text-zinc-400"}`} data-testid={`cost-jobfee-amount-${i}`}>${feeFor(r)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {rows && rows.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-zinc-700">Mileage</div>
                <MileageLegs legs={shownLegs} minLeg={minLeg} perMile={rates?.per_mile ?? 0.76} />
                {home && !home.set && <p className="text-[11px] font-semibold text-red-700" data-testid="cost-legs-no-home">No Home address saved — set it in the Daily Report's Home / Start box so Home legs can be worked out.</p>}
              </div>
            )}

            {rows && <Reimbursements items={reimb} setItems={setReimb} defaultDate={to} />}

            {rows && (rows.length > 0 || reimb.length > 0) && (
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-end gap-3">
                  <label className="text-xs text-zinc-600">Hourly rate
                    <div className="relative mt-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-zinc-500">$</span>
                      <Input type="number" min="0" step="0.01" inputMode="decimal" placeholder="0.00" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} className="h-9 w-32 pl-6" data-testid="cost-hourly-rate" />
                    </div>
                  </label>
                  <span className="text-xs text-zinc-500 pb-2" data-testid="cost-selected-count">{chosen.length} of {rows.length} jobs selected · {num(chosen.reduce((a, r) => a + (Number(r.hours) || 0), 0))} hrs</span>
                </div>
                <Button onClick={generate} disabled={busy || (!chosen.length && !reimb.length)} className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cost-generate-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" />Generate Cost Report</>}
                </Button>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3 min-w-0 max-w-full" data-testid="cost-result">
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setResult(null)} data-testid="cost-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back to selection</Button>
              <span className="text-xs text-zinc-500">{from} to {to} · {result.lines.length} job{result.lines.length === 1 ? "" : "s"}</span>
            </div>
            <div className="border rounded-lg overflow-x-auto min-w-0 max-w-full">
              <table className="w-full text-xs" data-testid="cost-result-table">
                <thead className="bg-zinc-900 text-white">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Job</th>
                    <th className="p-2 text-right">Job fee</th>
                    <th className="p-2 text-right">Failed visit</th>
                    <th className="p-2 text-right">Poles</th>
                    <th className="p-2 text-right">Pole pay</th>
                    <th className="p-2 text-right">Miles driven</th>
                    <th className="p-2 text-right">Paid mi</th>
                    <th className="p-2 text-right">Mileage pay</th>
                    <th className="p-2 text-right">Footage</th>
                    <th className="p-2 text-right">Ft over {(result.rates?.free_feet ?? 1000).toLocaleString()}</th>
                    <th className="p-2 text-right">Footage pay</th>
                    <th className="p-2 text-right">Hours</th>
                    <th className="p-2 text-right">Hours pay</th>
                    <th className="p-2 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {result.lines.map((l, i) => (
                    <tr key={`${l.key}-${i}`} className="border-t" data-testid={`cost-line-${i}`}>
                      <td className="p-2 whitespace-nowrap">{l.date}</td>
                      <td className="p-2"><div className="font-medium">{l.job || "—"}</div><div className="text-[10px] text-zinc-500 truncate max-w-[220px]">{l.job_address}</div></td>
                      <td className="p-2 text-right">{l.job_fee_amount ? usd(l.job_fee_amount) : <span className="text-zinc-400">—</span>}</td>
                      <td className="p-2 text-right" data-testid={`cost-line-failed-${i}`}>{l.failed_visit_fee ? usd(l.failed_visit_fee) : <span className="text-zinc-400">—</span>}</td>
                      <td className="p-2 text-right">{num(l.poles)}</td>
                      <td className="p-2 text-right">{usd(l.pole_pay)}</td>
                      <td className="p-2 text-right">{num(l.miles)}</td>
                      <td className="p-2 text-right" data-testid={`cost-line-paid-miles-${i}`}>{num(l.paid_miles)}</td>
                      <td className="p-2 text-right">{usd(l.mile_pay)}</td>
                      <td className="p-2 text-right">{num(l.footage)}</td>
                      <td className="p-2 text-right">{num(l.excess_feet)}</td>
                      <td className="p-2 text-right">{usd(l.foot_pay)}</td>
                      <td className="p-2 text-right">{num(l.hours)}</td>
                      <td className="p-2 text-right">{usd(l.hours_pay)}</td>
                      <td className="p-2 text-right font-semibold" data-testid={`cost-line-total-${i}`}>{usd(l.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-emerald-50 font-semibold">
                  <tr className="border-t-2 border-emerald-600">
                    <td className="p-2" colSpan={2}>TOTAL</td>
                    <td className="p-2 text-right">{usd(result.subtotals.job_fee_amount)}</td>
                    <td className="p-2 text-right" data-testid="cost-total-failed">{usd(result.subtotals.failed_visit_fee)}</td>
                    <td className="p-2" />
                    <td className="p-2 text-right">{usd(result.subtotals.pole_pay)}</td>
                    <td className="p-2 text-right">{num(result.miles)}</td>
                    <td className="p-2 text-right" data-testid="cost-total-paid-miles">{num(result.paid_miles)}</td>
                    <td className="p-2 text-right">{usd(result.subtotals.mile_pay)}</td>
                    <td className="p-2" colSpan={2} />
                    <td className="p-2 text-right">{usd(result.subtotals.foot_pay)}</td>
                    <td className="p-2 text-right">{num(result.hours)}</td>
                    <td className="p-2 text-right">{usd(result.subtotals.hours_pay)}</td>
                    <td className="p-2 text-right text-emerald-700 text-sm" data-testid="cost-grand-total">{usd(result.total - (result.subtotals.reimbursements || 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="grid sm:grid-cols-[minmax(0,1fr)_280px] gap-3 items-start">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-zinc-700">Mileage</div>
                <MileageLegs legs={result.legs || []} minLeg={result.rates?.min_leg_miles ?? minLeg} perMile={result.rates?.per_mile ?? 0} showPay testId="cost-result-mileage" />
                <ReimbursementsResult items={result.reimbursements || []} />
                <div className="text-xs text-zinc-500">Hourly rate used: {usd(result.hourly_rate)} / hr — change it on the selection screen and generate again.</div>
              </div>
              <div className="border rounded-lg divide-y text-sm" data-testid="cost-summary">
                {[["Job fees", result.subtotals.job_fee_amount, "cost-sum-jobs"], ["Failed site visit fees", result.subtotals.failed_visit_fee, "cost-sum-failed"], ["Pole pay", result.subtotals.pole_pay, "cost-sum-poles"],
                  ["Footage pay", result.subtotals.foot_pay, "cost-sum-footage"], [`Mileage (${num(result.paid_miles)} mi × ${usd(result.rates?.per_mile ?? 0)})`, result.subtotals.mile_pay, "cost-sum-mileage"],
                  [`Hours (${num(result.hours)} hrs × ${usd(result.hourly_rate)})`, result.subtotals.hours_pay, "cost-sum-hours"],
                  [`Reimbursements (${result.reimbursements?.length || 0})`, result.subtotals.reimbursements, "cost-sum-reimb"]].map(([label, val, tid]) => (
                  <div key={tid} className="flex justify-between px-3 py-1.5"><span className="text-zinc-600">{label}</span><span className="font-medium" data-testid={tid}>{usd(val)}</span></div>
                ))}
                <div className="flex justify-between px-3 py-2 bg-emerald-50 font-bold"><span>Overall total</span><span className="text-emerald-700" data-testid="cost-sum-total">{usd(result.total)}</span></div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={download} disabled={busy} className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cost-download-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileSpreadsheet className="w-4 h-4 mr-1" />Download Excel</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
